/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import verificationService from '../../services/verificationService';
import { useVendorVerification } from '../../hooks/useVendorVerification';
import { 
  Shield, 
  FileText, 
  Building, 
  MapPin, 
  CheckCircle,
  Loader2,
  ArrowLeft
} from 'lucide-react';
import Button from '../../components/shared/Button';
import { useToastContext } from '../../contexts/ToastContext';
import { useServiceCategories } from '../../services/serviceCategoryService';
import { useCountries } from '../../services/countryService';
import Step1BusinessInfo from '../../components/vendor/verification/Step1BusinessInfo';
import Step2ContactLocation from '../../components/vendor/verification/Step2ContactLocation';
import Step3Documents from '../../components/vendor/verification/Step3Documents';
import Step4Review from '../../components/vendor/verification/Step4Review';
import AddPlatformModal from '../../components/vendor/verification/AddPlatformModal';

const VendorVerification = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isVendorVerified, isVerificationPending, isLoading, refreshVerificationStatus } = useVendorVerification();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { success, error: showError, info } = useToastContext();
  const hasNavigatedRef = useRef(false);
  const justSubmittedRef = useRef(false);
  
  // Redirect verified vendors away from verification page
  useEffect(() => {
    if (!isLoading && isVendorVerified && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      success('You have already completed verification!', 'success');
      navigate('/vendor');
    }
  }, [isLoading, isVendorVerified, navigate, success]);

  // Redirect vendors with pending verification away from verification page
  useEffect(() => {
    if (!isLoading && isVerificationPending && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      
      // Only show pending message if we didn't just submit
      if (!justSubmittedRef.current) {
        info('Verification Pending', 'Your verification is currently pending review. Please wait for admin approval.');
      }
      
      navigate('/vendor');
    }
  }, [isLoading, isVerificationPending, navigate, info]);

  // Fetch service categories
  // console.log('user', user);
  const {
    data: categoriesData,
    isLoading: categoriesLoading, 
    error: categoriesError 
  } = useServiceCategories({ active: true });
  
  const serviceCategories = categoriesData?.data?.data || [];

  const {
    data: countriesData,
    isLoading: countriesLoading,
    error: countriesError
  } = useCountries({ active: true });

  const countries = countriesData?.data || [];
  const [formData, setFormData] = useState({
    // Business Information
    business_name: '',
    service_categories: null,
    business_description: '',
    years_experience: '',
    specialties: [],
    
    // Contact Information
    business_phone: '',
    business_email: '',
    website: '',
    social_media: {},
    
    // Address Information
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
    
    // Business Documents
    business_license: null,
    insurance_certificate: null,
    id_document: null,
    
    // Additional Information
    insurance_info: '',
    business_hours: {
      monday: { available: true },
      tuesday: { available: true },
      wednesday: { available: true },
      thursday: { available: true },
      friday: { available: true },
      saturday: { available: true },
      sunday: { available: true }
    },
    team_size: '',
    // equipment_list: '',
    certifications: '',
    references: ''
  });

  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [showAddPlatformModal, setShowAddPlatformModal] = useState(false);
  const [newPlatformName, setNewPlatformName] = useState('');
  
  const steps = [
    { id: 1, title: 'Business', icon: Building },
    { id: 2, title: 'Contact', icon: MapPin },
    { id: 3, title: 'Documents', icon: FileText },
    { id: 4, title: 'Review', icon: CheckCircle }
 ];

  useEffect(() => {
    // Load existing vendor profile data if available, then auto-populate from user data
    if (user) {
      setFormData(prev => {
        const updated = { ...prev };
        
        // First, load vendor profile data if available
        if (user.vendor_profile) {
          Object.assign(updated, user.vendor_profile);
          updated.specialties = user.vendor_profile.specialties || [];
        }
        
        // Auto-populate contact fields from user data if fields are empty
        // Only populate if the field is currently empty (empty string, null, or undefined)
        if (!updated.business_email && user.email) {
          updated.business_email = user.email;
        }
        if (!updated.business_phone && user.phone) {
          updated.business_phone = user.phone;
        }
        
        // Also populate address fields if available and empty
        if (!updated.city && user.city) {
          updated.city = user.city;
        }
        if (!updated.state && user.state) {
          updated.state = user.state;
        }
        if (!updated.address && user.address) {
          updated.address = user.address;
        }
        if (!updated.zip_code && user.zip_code) {
          updated.zip_code = user.zip_code;
        }
        
        return updated;
      });
    }
  }, [user]);

  // Separate useEffect for country auto-selection
  useEffect(() => {
    if (user?.country_code) {
      console.log('Setting country from user data:', user.country_code);
      setFormData(prev => ({
        ...prev,
        country: user.country_code
      }));
    }
  }, [user?.country_code]);

  
  // console.log('user',  user?.country_code);
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSpecialtyToggle = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const handleSocialMediaChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [platform]: value
      }
    }));
  };

  const addSocialMediaPlatform = () => {
    setNewPlatformName('');
    setShowAddPlatformModal(true);
  };

  const handleAddPlatform = () => {
    if (newPlatformName && newPlatformName.trim()) {
      const normalizedPlatform = newPlatformName.toLowerCase().trim();
      if (!formData.social_media[normalizedPlatform]) {
        handleSocialMediaChange(normalizedPlatform, '');
        setShowAddPlatformModal(false);
        setNewPlatformName('');
      } else {
        showError('This platform already exists!');
      }
    }
  };

  const handleCancelAddPlatform = () => {
    setShowAddPlatformModal(false);
    setNewPlatformName('');
  };

  const removeSocialMediaPlatform = (platform) => {
    setFormData(prev => {
      const newSocialMedia = { ...prev.social_media };
      delete newSocialMedia[platform];
      return {
        ...prev,
        social_media: newSocialMedia
      };
    });
  };

  const toggleDayAvailability = (day) => {
    setFormData(prev => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          available: !prev.business_hours[day].available
        }
      }
    }));
  };

  const handleFileUpload = async (field, file) => {
    if (!file) return;

    // Validate file type and size
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        [field]: 'Please upload a valid image (JPEG, PNG) or PDF file'
      }));
      return;
    }

    if (file.size > maxSize) {
      setErrors(prev => ({
        ...prev,
        [field]: 'File size must be less than 10MB'
      }));
      return;
    }

    setUploadProgress(prev => ({ ...prev, [field]: 0 }));

    try {
      // Upload file to S3 immediately (before form submission)
      const uploadResult = await verificationService.uploadDocument(
        file, 
        field,
        (progress) => {
          // Update upload progress
          setUploadProgress(prev => ({ ...prev, [field]: progress }));
        }
      );
      
      // Handle single file uploads - URL is now stored in formData
      setFormData(prev => ({
        ...prev,
        [field]: {
          file: file,
          name: file.name,
          url: uploadResult.data.url, // S3 URL returned from backend
          filename: uploadResult.data.filename,
          size: uploadResult.data.size,
          mime_type: uploadResult.data.mime_type
        }
      }));

      setUploadProgress(prev => ({ ...prev, [field]: null }));
    } catch (error) {
      console.error('File upload error:', error);
      setErrors(prev => ({
        ...prev,
        [field]: error.response?.data?.message || 'Failed to upload file. Please try again.'
      }));
      setUploadProgress(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
        if (!formData.service_categories) newErrors.service_categories = 'Please select a service category';
        if (!formData.business_description.trim()) newErrors.business_description = 'Business description is required';
        if (formData.business_description.length > 500) newErrors.business_description = 'Business description must not exceed 500 characters';
        if (!formData.years_experience) newErrors.years_experience = 'Years of experience is required';
        if (formData.specialties.length === 0) newErrors.specialties = 'Please select at least one specialty';
        break;
      case 2:
        if (!formData.business_phone.trim()) newErrors.business_phone = 'Business phone is required';
        if (!formData.business_email.trim()) newErrors.business_email = 'Business email is required';
        if (!formData.address.trim()) newErrors.address = 'Business address is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.state.trim()) newErrors.state = 'State is required';
        if (!formData.zip_code.trim()) newErrors.zip_code = 'ZIP code is required';
        break;
      // case 3:
      //   if (!formData.business_license) newErrors.business_license = 'Business license is required';
      //   if (!formData.insurance_certificate) newErrors.insurance_certificate = 'Insurance certificate is required';
      //   if (!formData.tax_id_document) newErrors.tax_id_document = 'Tax ID document is required';
      //   break;
      default:
        // No validation for step 4 (review step)
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    justSubmittedRef.current = true;
    hasNavigatedRef.current = false; // Reset navigation flag
    
    try {
      // Prepare verification data for user_verifications table
      const verificationData = {
        user_id: user.id,
        verification_type: 'vendor_business',
        verification_status: 'pending',
        verification_data: {
          business_name: formData.business_name?.trim(),
          service_categories: formData.service_categories ? [formData.service_categories] : [],
          business_description: formData.business_description?.trim(),
          years_experience: formData.years_experience,
          specialties: formData.specialties || [],
          business_phone: formData.business_phone?.trim(),
          business_email: formData.business_email?.trim(),
          website: formData.website?.trim(),
          social_media: formData.social_media,
          address: formData.address?.trim(),
          city: formData.city?.trim(),
          state: formData.state?.trim(),
          zip_code: formData.zip_code?.trim(),
          country: formData.country,
          insurance_info: formData.insurance_info?.trim(),
          business_hours: formData.business_hours,
          team_size: formData.team_size?.trim(),
          certifications: formData.certifications?.trim(),
          references: formData.references?.trim()
        },
        verification_document_url: {
          business_license: formData.business_license?.url || null,
          insurance_certificate: formData.insurance_certificate?.url || null,
          id_document: formData.id_document?.url || null
        }
      };

      // Prepare vendor profile data for vendor_profiles table
      const vendorProfileData = {
        business_name: formData.business_name?.trim(),
        description: formData.business_description?.trim(),
        address: formData.address?.trim(),
        city: formData.city?.trim(),
        state: formData.state?.trim(),
        zip_code: formData.zip_code?.trim(),
        country: formData.country,
        website: formData.website?.trim(),
        social_media: formData.social_media,
        years_experience: parseInt(formData.years_experience) || 0,
        specialties: formData.specialties || [],
        insurance_info: formData.insurance_info?.trim(),
        business_hours: formData.business_hours,
        team_size: parseInt(formData.team_size) || null,
        certifications: formData.certifications?.trim() || null,
        references: formData.references?.trim() || null,
        service_categories: formData.service_categories ? [formData.service_categories] : [],
        is_verified: true // Auto-verify vendor after submission
      };

      // Submit verification and update profile in a single transaction
      await verificationService.submitVerificationWithProfile({
        verification_data: verificationData.verification_data,
        verification_document_url: verificationData.verification_document_url,
        vendor_profile_data: vendorProfileData
      });

      // Show success message
      success('Verification completed successfully! Your vendor account has been verified.');
      
      // Refresh verification status in the hook
      await refreshVerificationStatus();
      
      // Let useEffect handle navigation when isVendorVerified becomes true
      // Give it a moment for the status to update, then navigate if needed
      setTimeout(() => {
        if (!hasNavigatedRef.current) {
          hasNavigatedRef.current = true;
          navigate('/vendor');
        }
        justSubmittedRef.current = false; // Reset after navigation
      }, 300);
    } catch (error) {
      console.error('Verification submission error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit verification. Please try again.';
      showError(errorMessage);
      justSubmittedRef.current = false; // Reset on error
    } finally {
      setIsSubmitting(false);
    }
  };

  // Skeleton Loading Components
  const VerificationSkeleton = () => (
    <div className="min-h-screen bg-gray-50">
      {/* Header Skeleton */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between animate-pulse">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-200 rounded-lg"></div>
              <div className="h-5 w-24 bg-gray-200 rounded"></div>
      </div>
            <div className="h-4 w-32 bg-gray-200 rounded hidden sm:block"></div>
        </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Page Header Skeleton */}
        <div className="mb-4 sm:mb-8 animate-pulse">
          <div className="h-4 w-32 bg-gray-200 rounded mb-3 sm:mb-4"></div>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-64 bg-gray-200 rounded"></div>
        </div>
      </div>
        </div>

        {/* Progress Steps Skeleton */}
        <div className="mb-4 sm:mb-8 overflow-x-auto">
          <div className="flex items-center justify-between w-full min-w-max sm:min-w-0 animate-pulse">
            {[1, 2, 3, 4].map((step, index) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center flex-1 relative min-w-[60px] sm:min-w-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full border-2 border-gray-200"></div>
                  <div className="mt-1.5 sm:mt-2">
                    <div className="h-3 w-16 bg-gray-200 rounded"></div>
        </div>
      </div>
                {index < 3 && (
                  <div className="flex-1 mx-1 sm:mx-3 h-0.5 relative -mt-4 sm:-mt-6 min-w-[20px] sm:min-w-0">
                    <div className="absolute inset-0 rounded-full bg-gray-200"></div>
        </div>
        )}
              </React.Fragment>
            ))}
      </div>
    </div>

        {/* Form Content Skeleton */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8 animate-pulse">
    <div className="space-y-6">
            {/* Title Skeleton */}
      <div>
              <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-96 bg-gray-200 rounded"></div>
      </div>

            {/* Form Fields Skeleton */}
          <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
                  <div className="h-10 w-full bg-gray-200 rounded"></div>
              </div>
            ))}
      </div>

            {/* Select/Dropdown Skeleton */}
      <div>
              <div className="h-4 w-40 bg-gray-200 rounded mb-2"></div>
              <div className="h-10 w-full bg-gray-200 rounded"></div>
      </div>

            {/* Textarea Skeleton */}
        <div>
              <div className="h-4 w-36 bg-gray-200 rounded mb-2"></div>
              <div className="h-24 w-full bg-gray-200 rounded"></div>
        </div>

            {/* Checkboxes/Buttons Skeleton */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="h-12 bg-gray-200 rounded-lg"></div>
              ))}
      </div>
    </div>
      </div>

        {/* Navigation Buttons Skeleton */}
        <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-4 sm:mt-8 animate-pulse">
          <div className="h-10 w-24 bg-gray-200 rounded order-2 sm:order-1"></div>
          <div className="h-10 w-20 bg-gray-200 rounded order-1 sm:order-2"></div>
          </div>
                </div>
              </div>
  );

  const renderCurrentStep = () => {
    // Show skeleton if categories or countries are loading on their respective steps
    if (currentStep === 1 && categoriesLoading) {
      return (
        <div className="space-y-6 animate-pulse">
        <div>
            <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 w-96 bg-gray-200 rounded"></div>
          </div>
      <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
                <div className="h-10 w-full bg-gray-200 rounded"></div>
              </div>
            ))}
      </div>
    </div>
  );
    }

    if (currentStep === 2 && countriesLoading) {
      return (
        <div className="space-y-6 animate-pulse">
        <div>
            <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 w-96 bg-gray-200 rounded"></div>
            </div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i}>
                <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
                <div className="h-10 w-full bg-gray-200 rounded"></div>
            </div>
            ))}
      </div>
    </div>
  );
    }

    switch (currentStep) {
      case 1:
    return (
          <Step1BusinessInfo
            formData={formData}
            errors={errors}
            serviceCategories={serviceCategories}
            categoriesLoading={categoriesLoading}
            categoriesError={categoriesError}
            handleInputChange={handleInputChange}
            handleSpecialtyToggle={handleSpecialtyToggle}
          />
        );
      case 2:
        return (
          <Step2ContactLocation
            formData={formData}
            errors={errors}
            countries={countries}
            countriesLoading={countriesLoading}
            countriesError={countriesError}
            handleInputChange={handleInputChange}
            handleSocialMediaChange={handleSocialMediaChange}
            removeSocialMediaPlatform={removeSocialMediaPlatform}
            addSocialMediaPlatform={addSocialMediaPlatform}
            showAddPlatformModal={showAddPlatformModal}
            setShowAddPlatformModal={setShowAddPlatformModal}
            newPlatformName={newPlatformName}
            setNewPlatformName={setNewPlatformName}
            handleAddPlatform={handleAddPlatform}
            handleCancelAddPlatform={handleCancelAddPlatform}
          />
        );
      case 3:
        return (
          <Step3Documents
            formData={formData}
            errors={errors}
            uploadProgress={uploadProgress}
            handleFileUpload={handleFileUpload}
            handleInputChange={handleInputChange}
            toggleDayAvailability={toggleDayAvailability}
          />
        );
      case 4:
        return (
          <Step4Review
            formData={formData}
            serviceCategories={serviceCategories}
            countries={countries}
          />
        );
      default:
        return null;
    }
  };

  // Show skeleton loading state while checking verification status
  if (isLoading) {
    return <VerificationSkeleton />;
  }

  // Show verified state - redirect will happen in useEffect
  if (isVendorVerified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-600 mx-auto mb-3 sm:mb-4" />
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">Already Verified</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">You have already completed the verification process.</p>
          <p className="text-xs sm:text-sm text-gray-500">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  // Show pending verification state - redirect will happen in useEffect
  if (isVerificationPending) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-600 mx-auto mb-3 sm:mb-4 animate-spin" />
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">Verification Pending</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">Your verification is currently under review by our team.</p>
          <p className="text-xs sm:text-sm text-gray-500 mb-4">You will receive an email notification once your verification is complete.</p>
          <p className="text-xs sm:text-sm text-gray-500">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-medium text-xs sm:text-sm">PB</span>
              </div>
              <span className="text-base sm:text-lg font-medium text-gray-900">PhotoBooth</span>
            </div>
            <div className="text-xs sm:text-sm text-gray-600 hidden sm:block">
              Vendor Verification
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Page Header */}
        <div className="mb-4 sm:mb-8">
          <button
            onClick={() => navigate('/vendor')}
            className="flex items-center text-sm sm:text-base text-gray-600 hover:text-gray-900 mb-3 sm:mb-4"
          >
            <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            Back to Dashboard
          </button>
          
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Vendor Verification</h1>
              <p className="text-xs sm:text-sm text-gray-600">Complete your business verification to access all features</p>
            </div>
          </div>
        </div>

        {/* Progress Steps - Compact Design */}
        <div className="mb-4 sm:mb-8 overflow-x-auto">
          <div className="flex items-center justify-between w-full min-w-max sm:min-w-0">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-1 relative min-w-[60px] sm:min-w-0">
                    {/* Step Circle */}
                    <div className="relative z-10">
                      <div className={`relative flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 transition-all duration-300 ${
                        isCompleted 
                          ? 'bg-black border-black text-white shadow-md' 
                          : isActive 
                            ? 'bg-black border-black text-white shadow-lg ring-2 ring-black/20 scale-105' 
                            : 'bg-white border-gray-300 text-gray-400'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                        ) : (
                          <StepIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${isActive ? 'text-white' : ''}`} />
                        )}
                      </div>
                    </div>
                    
                    {/* Step Title */}
                    <div className="mt-1.5 sm:mt-2 text-center">
                      <p className={`text-[10px] sm:text-xs font-medium transition-colors duration-300 ${
                        isActive 
                          ? 'text-black' 
                          : isCompleted 
                            ? 'text-gray-600' 
                            : 'text-gray-400'
                      }`}>
                        {step.title}
                      </p>
                    </div>
                  </div>
                  
                  {/* Connecting Line */}
                  {index < steps.length - 1 && (
                    <div className="flex-1 mx-1 sm:mx-3 h-0.5 relative -mt-4 sm:-mt-6 min-w-[20px] sm:min-w-0">
                      <div className="absolute inset-0 rounded-full bg-gray-200" />
                      <div 
                        className={`absolute inset-0 rounded-full transition-all duration-500 ease-in-out ${
                          isCompleted ? 'bg-black' : 'bg-transparent'
                        }`}
                        style={{ 
                          width: isCompleted ? '100%' : '0%' 
                        }}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
          {renderCurrentStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-4 sm:mt-8">
          <Button
            onClick={handlePrevious}
            variant="outline"
            disabled={currentStep === 1}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Previous
          </Button>
          
          <div className="flex space-x-3 order-1 sm:order-2">
            {currentStep < 4 ? (
              <Button onClick={handleNext} className="w-full sm:w-auto">
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center justify-center w-full sm:w-auto"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit for Verification'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Add Platform Modal */}
      <AddPlatformModal
        isOpen={showAddPlatformModal}
        newPlatformName={newPlatformName}
        setNewPlatformName={setNewPlatformName}
        onAdd={handleAddPlatform}
        onCancel={handleCancelAddPlatform}
      />
    </div>
  );
};

export default VendorVerification;
